import React, { useState } from 'react'
import { X } from 'lucide-react'
import { COOKIE_TYPES } from '../../lib/constants'
import { Button } from '../ui/button'

interface CookieModalProps {
  onAcceptAll: () => void
  onSavePreferences: (preferences: { analytics: boolean; advertising: boolean }) => void
  initialPreferences: { analytics: boolean; advertising: boolean }
}

export function CookieModal({ onAcceptAll, onSavePreferences, initialPreferences }: CookieModalProps) {
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState(initialPreferences)

  const handleToggle = (type: 'analytics' | 'advertising') => {
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  // Set both checkboxes to checked when showing preferences
  const handleShowPreferences = () => {
    setPreferences({
      analytics: true,
      advertising: true
    })
    setShowPreferences(true)
  }

  if (!showPreferences) {
    return (
      // A card at the foot of the screen, with no overlay, so the page (and the homepage animation) stays in view
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 sm:justify-end sm:p-6">
        <div className="pointer-events-auto w-full max-w-md rounded-lg bg-card p-6 shadow-lg border border-border dark:border-border/80 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Cookie Preferences</h2>
            <p className="text-sm text-muted-foreground">
              We use cookies to enhance your browsing experience and analyze our traffic. 
              Please choose your preferences below.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={onAcceptAll} className="flex-1">
                Accept All
              </Button>
              <Button variant="outline" onClick={handleShowPreferences} className="flex-1">
                Manage Preferences
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-card p-6 shadow-lg border border-border dark:border-border/40">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Cookie Settings</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowPreferences(false)}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="space-y-4">
          <div className="space-y-4">
            {Object.entries(COOKIE_TYPES).map(([key, { name, description }]) => (
              <div key={key} className="flex items-start space-x-4">
                <div className="flex h-6 items-center">
                  <input
                    type="checkbox"
                    checked={preferences[key as keyof typeof preferences]}
                    onChange={() => handleToggle(key as keyof typeof preferences)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </div>
                <div className="space-y-1">
                  <p className="font-medium">{name}</p>
                  <p className="text-sm text-muted-foreground">{description}</p>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={() => onSavePreferences(preferences)} className="w-full">
            Save Preferences
          </Button>
        </div>
      </div>
    </div>
  )
}